import { FC, useState, useEffect, useRef, useCallback } from "react";
import styles from "./Chat.module.scss";
import { MessageForm } from "./Form";
import { Message, MessageTwo } from "./Message";
import { useAppSelector } from "../../services/hooks";
import { CreateMessageDto, MessageDto } from "./Services/socket-type";
import { cryptLib } from "./Services/utils";
import { IUserData, TUserType } from "../../types/user";
import { useNavigate } from "react-router-dom";
import { getUserById } from "../../api/user";
import Loader from "../Loader/Loader";
import { uploadPhoto } from "../../api/chat";
import { MessageType } from "../../types/chat";

// Format date for message grouping
const formatDateForChat = (dateString: string): string => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return "Today";
  } else if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  } else {
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }
};

interface ChatItemProps {
  interlocutorId: number;
  conversationId: number | null;
  messages: MessageDto[];
  sendMessage: (msg: CreateMessageDto) => void;
  isTyping: boolean;
  isBlocked: boolean;
  isBlocker: boolean;
  onlineStatus: boolean;
  onUnblockUser?: () => void;
  isConnected: boolean;
  isLoadingDialogs: boolean;
}

export const ChatItem: FC<ChatItemProps> = ({
  interlocutorId,
  messages,
  sendMessage,
  isTyping,
  isBlocked,
  isBlocker,
  onlineStatus,
  isConnected,
  conversationId,
  isLoadingDialogs,
}) => {
  const { user } = useAppSelector((state) => state.user);
  const [text, setText] = useState<string>("");
  const [isLoadingUser, setIsLoadingUser] = useState<boolean>(false);
  const [loadUserError, setLoadUserError] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [senderData, setSenderData] = useState<{
    interlocutorName: string;
    interlocutorAvatar: null | string;
    interlocutorType: TUserType | "unknow";
  }>({
    interlocutorName: "User",
    interlocutorAvatar: null,
    interlocutorType: "unknow",
  });

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();

    const timeoutId = setTimeout(scrollToBottom, 700);

    // Установка наблюдателя за изменениями размера контейнера сообщений
    if (messagesContainerRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        scrollToBottom();
      });

      resizeObserver.observe(messagesContainerRef.current);

      // Очистка при размонтировании
      return () => {
        clearTimeout(timeoutId);
        resizeObserver.disconnect();
      };
    }

    return () => clearTimeout(timeoutId);
  }, [messages, isTyping, interlocutorId]);

  useEffect(() => {
    window.addEventListener("load", scrollToBottom);

    return () => {
      window.removeEventListener("load", scrollToBottom);
    };
  }, []);

  // Fetch interlocutor data
  const getSenderData = useCallback(async () => {
    try {
      setIsLoadingUser(true);
      const data: IUserData = await getUserById(interlocutorId);
      if (data) {
        setSenderData({
          interlocutorAvatar: data.profileAvatar?.avatarUrl
            ? data.profileAvatar.avatarUrl
            : null,
          interlocutorType: data.type,
          interlocutorName: data.firstName,
        });
      }
    } catch (err) {
      setLoadUserError(true);
    } finally {
      setIsLoadingUser(false);
    }
  }, [interlocutorId]);

  // Fetch interlocutor data on component mount or when ID changes
  useEffect(() => {
    if (interlocutorId) {
      getSenderData();
      setSelectedFile(null);
    }
  }, [interlocutorId, getSenderData]);

  // Group messages by date
  const groupedMessages = messages.reduce(
    (acc, message) => {
      const date = formatDateForChat(message.createdAt);
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(message);
      return acc;
    },
    {} as Record<string, MessageDto[]>,
  );

  // Message submission handler
  const handleSubmit = async (e: React.FormEvent) => {
    if (conversationId) {
      e.preventDefault();
      try {
        if (text.trim()) {
          setIsSending(true);

          // Создаем объект для отправки сообщения
          const messageDto: CreateMessageDto = {
            recipientId: interlocutorId,
            text: cryptLib.encrypt(text.trim()),
            conversationId: conversationId,
            type: "text",
          };

          sendMessage(messageDto);
          setText("");
        }
        if (selectedFile !== null) {
          setIsSending(true);

          const formData = new FormData();
          formData.append("file", selectedFile);
          formData.append("bucket", "chat-attachments"); // используйте имя бакета из вашей конфигурации
          const response: {
            fileUrl: string;
            fileSize: string;
            type: string;
          } = await uploadPhoto(formData);

          const messageDto: CreateMessageDto = {
            recipientId: interlocutorId,
            attachmentUrl: response.fileUrl,
            conversationId: conversationId,
            type: response.type as MessageType,
            fileSize: response.fileSize,
          };

          // Отправляем сообщение через сокет
          sendMessage(messageDto);

          // Сбрасываем выбранный файл
          setSelectedFile(null);
        }
      } catch (error) {
        console.error("Failed to send message:", error);
      } finally {
        setIsSending(false);
      }
    }
  };

  // Determine if avatar should be shown (for message grouping)
  const shouldShowAvatar = (messages: MessageDto[], index: number) => {
    if (index === 0) return true;
    return messages[index].senderId !== messages[index - 1].senderId;
  };

  // If no active conversation, show placeholder

  // Определение текста статуса и класса стилей
  const getStatusInfo = () => {
    if (isTyping && !isBlocked) {
      return {
        text: "typing...",
        className: styles.statusTyping,
      };
    } else if (isBlocked) {
      return {
        text: isBlocker ? "Blocked" : "You are blocked",
        className: styles.statusBlocked,
      };
    } else if (onlineStatus) {
      return {
        text: "online",
        className: styles.statusOnline,
      };
    } else {
      return {
        text: "offline",
        className: styles.statusOffline,
      };
    }
  };

  if (
    senderData.interlocutorType === "unknow" ||
    isLoadingDialogs ||
    isLoadingUser
  ) {
    return (
      <div className="loader-container">
        <Loader color="#ac2386" size={40} />
      </div>
    );
  } else if (!interlocutorId) {
    return (
      <div className={styles.emptyChat}>
        <div className={styles.emptyChatIcon}>💬</div>
        <p>No conversations available</p>
        <p>Select chat</p>
      </div>
    );
  } else if (loadUserError) {
    return (
      <div className={styles.emptyChat}>
        <div className={styles.emptyChatIcon}>🚫</div>
        <p>User not found.</p>
      </div>
    );
  }

  const statusInfo = getStatusInfo();
  return (
    <div className={styles.chat}>
      <div className={styles.chatHeader}>
        <div
          className={styles.chatHeaderAvatar}
          onClick={() => {
            if (senderData.interlocutorType === "model") {
              navigate(`/models/${interlocutorId}`);
            }
          }}
        >
          {senderData.interlocutorAvatar ? (
            <img
              src={senderData.interlocutorAvatar}
              alt={senderData.interlocutorName}
            />
          ) : (
            <div className={styles.avatarPlaceholder}>
              {senderData.interlocutorName.charAt(0).toUpperCase()}
            </div>
          )}
          {onlineStatus && (
            <span className={styles.headerOnlineIndicator}></span>
          )}
          {isBlocked && (
            <span
              className={styles.headerBlockedIndicator}
              title={isBlocker ? "User blocked" : "You are blocked"}
            >
              🚫
            </span>
          )}
        </div>
        <div className={styles.chatHeaderInfo}>
          <h3>{senderData.interlocutorName}</h3>
          <span
            className={`${styles.chatHeaderStatus} ${statusInfo.className}`}
          >
            {statusInfo.text}
          </span>
        </div>
      </div>

      {!isConnected && (
        <div className={styles.connectionStatus}>
          <span className={styles.connectionDot}></span>
          <span>Connecting to chat server...</span>
        </div>
      )}

      {/* Messages list */}
      <div ref={messagesContainerRef} className={styles.messageList}>
        {isBlocked && !isBlocker ? (
          <div className={styles.blockedMessage}>
            <div className={styles.blockedIcon}>🚫</div>
            <p>You are blocked</p>
            <p>You cannot send or view messages</p>
          </div>
        ) : messages.length === 0 ? (
          <div className={styles.emptyChat}>
            <div className={styles.emptyChatIcon}>💬</div>
            <p>No messages yet</p>
            <p>Send a message to start the conversation</p>
          </div>
        ) : (
          Object.entries(groupedMessages).map(([date, dailyMessages]) => (
            <div key={date} className={styles.dayGroup}>
              <div className={styles.dateHeader}>
                <span>{date}</span>
              </div>

              {dailyMessages.map((item, index) => (
                <div key={item.id} className={styles.messageWrapper}>
                  {user?.id === item.senderId ? (
                    <MessageTwo
                      message={cryptLib.decrypt(item.text).trim()}
                      time={new Date(item.createdAt).toLocaleTimeString(
                        "en-US",
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        },
                      )}
                      isRead={item.readAt !== null}
                      attachmentUrl={item.attachmentUrl}
                    />
                  ) : (
                    <Message
                      message={cryptLib.decrypt(item.text).trim()}
                      attachmentUrl={item.attachmentUrl}
                      time={new Date(item.createdAt).toLocaleTimeString(
                        "en-US",
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        },
                      )}
                      avatar={
                        shouldShowAvatar(dailyMessages, index)
                          ? senderData.interlocutorAvatar
                            ? senderData.interlocutorAvatar
                            : undefined
                          : undefined
                      }
                      userName={
                        shouldShowAvatar(dailyMessages, index)
                          ? senderData.interlocutorName
                          : undefined
                      }
                    />
                  )}
                </div>
              ))}
            </div>
          ))
        )}

        {isTyping && !isBlocked && (
          <div className={styles.typingIndicatorContainer}>
            <div className={styles.typingIndicator}>
              <span className={styles.typingName}>
                {senderData.interlocutorName}
              </span>
              <div className={styles.typingDots}>
                <span className={styles.typingDot}></span>
                <span className={styles.typingDot}></span>
                <span className={styles.typingDot}></span>
              </div>
            </div>
          </div>
        )}

        {isBlocked && isBlocker && (
          <div className={styles.blockedMessageInfo}>
            <p>User is blocked. You cannot send messages.</p>
          </div>
        )}
      </div>

      {/* Message form - disabled when blocked */}
      <MessageForm
        sendMessage={handleSubmit}
        text={text}
        setText={setText}
        interlocutor={interlocutorId}
        isSending={isSending}
        isConnected={isConnected}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        isDisabled={isBlocked || !conversationId} // Отключаем форму отправки при блокировке
      />
    </div>
  );
};
